// src/components/reports/RoadmapModal.jsx
//
// Recommendations & roadmap — the managed data behind the report's LAST section.
//
// Two sources in one view:
//   • BUDGETED — the client's one-off project/hardware lines for the financial
//     year, read from the Budget section's own blob. Read-only text: the budget
//     is the source of truth, and only the STATE (scheduled / which month) is
//     managed here.
//   • RECOMMENDATIONS — AI-drafted or hand-written. ⛔ NOTHING reaches the
//     client's report until it is APPROVED here. Drafting stores a draft; it
//     does not publish it. That is the whole fix for the old section, which
//     shipped three hardcoded "proposed projects" to every client alike.
//
// CLIENT-FACING: everything approved here is printed on the client's report and
// on their share link. Said on screen, because this modal opens from the same
// row as Credential hygiene, which never leaves the app.
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  getRoadmap, setItemState, addRec, updateRec, deleteRec, draftRecs,
} from "../../api/roadmapApi";
import { quoteStateView, quotedLabel, isQuoted } from "./quoteState";

const btn = (primary) => ({
  padding: "6px 11px", borderRadius: 7, fontSize: 11.5, fontWeight: 600, cursor: "pointer",
  border: primary ? "none" : "1px solid var(--border,#d0d0d0)",
  background: primary ? "var(--dt-secondary,#0b80a5)" : "var(--card,#fff)",
  color: primary ? "#fff" : "var(--text,#222)",
});
const inp = {
  width: "100%", padding: "7px 9px", borderRadius: 7, fontSize: 12,
  border: "1px solid var(--border,#d0d0d0)", background: "var(--card,#fff)",
  color: "var(--text,#222)", fontFamily: "inherit",
};
const chip = (bg, fg) => ({
  fontSize: 9.5, fontWeight: 800, letterSpacing: .4, textTransform: "uppercase",
  padding: "2px 7px", borderRadius: 20, background: bg, color: fg, whiteSpace: "nowrap",
});

const money = (n) => (Number(n) > 0 ? `$${Math.round(Number(n)).toLocaleString("en-AU")}` : "—");

/* ⚠ Built from the FY string, never `new Date(period)`: that parses as UTC
   midnight and renders as the previous month in Perth, which would offer Craig
   a month list shifted by one against what the report prints. */
const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const monthLabel = (p) => {
  const m = /^(\d{4})-(\d{2})$/.exec(String(p || ""));
  return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1]}` : "Not scheduled";
};

/** The 12 months of a financial year, in FY order (Jul→Jun). */
function fyMonths(fy) {
  const m = /^FY(\d{2})-(\d{2})$/.exec(String(fy || ""));
  if (!m) return [];
  const start = 2000 + Number(m[1]);
  return Array.from({ length: 12 }, (_, i) => {
    const month = i < 6 ? 7 + i : i - 5;
    const year = i < 6 ? start : start + 1;
    return `${year}-${String(month).padStart(2, "0")}`;
  });
}

export default function RoadmapModal({
  companyId, companyName, period, periodLabel, onClose, toast, onCreateQuote,
}) {
  const [data, setData] = useState(null);          // null = loading
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);    // { id, title, detail, cost, kind }
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    getRoadmap(companyId)
      .then(setData)
      .catch((e) => toast(e?.response?.data?.error || "Could not load the roadmap", "err"));
  }, [companyId, toast]);

  useEffect(load, [load]);

  const months = useMemo(() => fyMonths(data?.fy), [data?.fy]);
  const items = data?.items || [];
  const recs = data?.recs || [];
  const approvedCount = recs.filter((r) => r.approved).length;

  const guard = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); load(); if (okMsg) toast(okMsg, "ok"); }
    catch (e) { toast(e?.response?.data?.error || e.message || "That didn't work", "err"); }
    finally { setBusy(false); }
  };

  /* ⚠ null clears the month; "" from a <select> would be sent as a string and
     rejected. The two are mapped here rather than in the API client so the
     wire shape stays exactly what the backend documents. */
  const setMonth = (lineId, value) =>
    guard(() => setItemState(companyId, lineId, { targetMonth: value || null }));

  const promote = (item) =>
    guard(() => setItemState(companyId, item.id, {
      status: item.status === "project" ? "budgeted" : "project",
    }), item.status === "project" ? "Moved back to budgeted" : "Scheduled as a project");

  const draft = () =>
    guard(async () => {
      // "2 dropped: 1 server content, 1 ungrounded figure" — or "" when nothing was dropped.
      const dropReasons = (rejected) => {
        const list = Array.isArray(rejected) ? rejected : [];
        if (!list.length) return "";
        const server = list.filter((r) => r && r.term).length;
        const figures = list.filter((r) => r && !r.term && (r.figures || r.reasons)).length;
        const other = list.length - server - figures;
        const parts = [
          server ? `${server} server content` : null,
          figures ? `${figures} ungrounded figure${figures === 1 ? "" : "s"}` : null,
          other ? `${other} other` : null,
        ].filter(Boolean);
        return `${list.length} dropped: ${parts.join(", ")}`;
      };
      const out = await draftRecs(companyId, period);
      const n = out?.drafts?.length || 0;
      /* The gate drops for TWO reasons and says which: `{term}` is server
         content, `{figures}` is a figure the facts never supplied (the common
         one since the voice went in). Craig reads this line to decide whether
         to re-draft, so it must not call an ungrounded figure "server content". */
      const reasons = dropReasons(out?.rejected);
      toast(
        n
          ? `${n} draft${n === 1 ? "" : "s"} added — review and approve${reasons ? ` (${reasons})` : ""}`
          : `The model had nothing to suggest from this month's data${reasons ? ` (${reasons})` : ""}`,
        n ? "ok" : "warn",
      );
    });

  /* ⚠ CONFIRMED, because this creates a quote AND emails Craig. It is also
     reachable mid-meeting from the Meeting Notes surface, where an accidental
     click is worse than a wasted one. */
  const quoteFrom = async (item) => {
    if (!window.confirm(`Create a quote for "${item.title}" and notify Craig?`)) return;
    try {
      await onCreateQuote({ ...item, companyId, companyName });
      toast(`Quote started from "${item.title}"`, "ok");
      onClose();
    } catch (e) {
      /* The server's idempotency guard fired — the item was already quoted, so
         nothing was created and no second email went out. That is a correct
         outcome, not an error, so it is reported as such and the view refreshed
         to show the badge the user was evidently not seeing. */
      if (e.alreadyQuoted) { toast("Already quoted — no second quote was created", "warn"); load(); return; }
      toast(e.message || "Could not start a quote", "err");
    }
  };

  const Row = ({ item, children, right }) => (
    <div style={{
      border: "1px solid var(--border,#e2e2e2)", borderRadius: 9, padding: "9px 11px",
      marginBottom: 7, background: "var(--card,#fff)",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700 }}>{item.title}</div>
          {item.detail && (
            <div style={{ fontSize: 11, color: "var(--muted,#666)", marginTop: 2 }}>{item.detail}</div>
          )}
        </div>
        {right}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 7, marginTop: 8 }}>
        {children}
      </div>
    </div>
  );

  /* ⛔ ONCE QUOTED, THE BUTTON IS GONE — replaced by a badge, not left live.
     A second live button invites the duplicate the server would then refuse,
     which reads to the user as the button being broken. */
  const quoteBtn = (item) => {
    if (isQuoted(item.quoteState)) {
      const v = quoteStateView(item.quoteState);
      return <span style={chip(v.bg, v.fg)}>{quotedLabel(item)}</span>;
    }
    return (
      <button style={{ ...btn(false), padding: "3px 8px", fontSize: 10.5 }} disabled={busy}
        onClick={() => quoteFrom(item)}
        title="Creates a quote pre-filled from this item and emails Craig.">
        🧾 Create quote
      </button>
    );
  };

  return (
    <div onClick={() => !busy && onClose()}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 620, display: "grid", placeItems: "center", padding: 14 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg,#f7f7f7)", borderRadius: 12, padding: 18, width: "min(860px, 96vw)",
          maxHeight: "90vh", overflowY: "auto", boxShadow: "0 18px 50px rgba(0,0,0,.3)",
        }}>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <div style={{ fontSize: 15, fontWeight: 800, flex: 1 }}>
            Recommendations &amp; roadmap — {companyName}
          </div>
          <button style={btn(false)} onClick={onClose} disabled={busy}>Close</button>
        </div>
        <div style={{ fontSize: 11, color: "var(--muted,#666)", marginBottom: 14 }}>
          {periodLabel} · financial year {data?.fy || "…"} ·{" "}
          <b>everything approved here is printed on the client's report.</b>{" "}
          Generating the report freezes this month, so later edits will not change a report already sent.
        </div>

        {data === null && <div style={{ fontSize: 12 }}>Loading…</div>}

        {data && (
          <>
            {/* ── Budgeted items ─────────────────────────────────────────── */}
            <div style={{ fontSize: 12.5, fontWeight: 800, margin: "4px 0 6px" }}>
              Budget — one-off projects &amp; hardware ({items.length})
            </div>
            {!items.length && (
              <div style={{ fontSize: 11.5, color: "var(--muted,#666)", marginBottom: 12 }}>
                No one-off project or hardware lines in this client's {data.fy} budget.
                Add them in the Budget section and they appear here.
              </div>
            )}
            {items.map((item) => (
              <Row key={item.id} item={item}
                right={
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={chip(item.status === "project" ? "#e7f2fb" : "#fff4e2",
                      item.status === "project" ? "#2a5f9e" : "#b7791f")}>
                      {item.status === "project" ? "Scheduled" : "Budgeted"}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 700 }}>{money(item.cost)}</span>
                  </div>
                }>
                <button style={{ ...btn(item.status !== "project"), padding: "3px 8px", fontSize: 10.5 }}
                  disabled={busy} onClick={() => promote(item)}>
                  {item.status === "project" ? "↩ Back to budgeted" : "→ Schedule as project"}
                </button>
                <select style={{ ...inp, width: "auto", padding: "3px 7px", fontSize: 10.5 }}
                  value={item.targetMonth || ""} disabled={busy}
                  onChange={(e) => setMonth(item.id, e.target.value)}>
                  <option value="">Not scheduled</option>
                  {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
                </select>
                {quoteBtn(item)}
              </Row>
            ))}

            {/* ── Recommendations ────────────────────────────────────────── */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "18px 0 6px" }}>
              <div style={{ fontSize: 12.5, fontWeight: 800, flex: 1 }}>
                Recommendations ({approvedCount} approved of {recs.length})
              </div>
              <button style={btn(false)} disabled={busy} onClick={() => setAdding(true)}>+ Add</button>
              <button style={btn(true)} disabled={busy} onClick={draft}
                title="Drafts suggestions from this month's real figures. Drafts are NOT shown to the client until approved.">
                🤖 Draft with AI
              </button>
            </div>
            <div style={{ fontSize: 11, color: "var(--muted,#666)", marginBottom: 8 }}>
              Drafts stay in the app. Only what you approve appears on the client's report.
              Suggestions about servers are refused — they are handled outside this report.
            </div>

            {adding && (
              <RecForm busy={busy}
                onCancel={() => setAdding(false)}
                onSave={(body) => guard(() => addRec(companyId, body), "Recommendation added")
                  .then(() => setAdding(false))} />
            )}

            {!recs.length && !adding && (
              <div style={{ fontSize: 11.5, color: "var(--muted,#666)" }}>
                Nothing yet. Draft some from this month's figures, or add one by hand.
              </div>
            )}

            {recs.map((r) => (editing?.id === r.id ? (
              <RecForm key={r.id} busy={busy} initial={r}
                onCancel={() => setEditing(null)}
                onSave={(body) => guard(() => updateRec(companyId, r.id, body), "Recommendation updated")
                  .then(() => setEditing(null))} />
            ) : (
              <Row key={r.id} item={r}
                right={
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={chip(r.approved ? "#e9f6ee" : "#f1f1f1", r.approved ? "#00631f" : "#666")}>
                      {r.approved ? "✓ On the report" : "Draft — not shown"}
                    </span>
                    {r.source === "ai" && <span style={chip("#eef4fb", "#2a5f9e")}>AI</span>}
                    <span style={{ fontSize: 12, fontWeight: 700 }}>{money(r.cost)}</span>
                  </div>
                }>
                <span style={chip("#f4f4f4", "#555")}>{r.kind === "quick-win" ? "Quick win" : "Suggested"}</span>
                <button style={{ ...btn(!r.approved), padding: "3px 8px", fontSize: 10.5 }} disabled={busy}
                  onClick={() => guard(() => updateRec(companyId, r.id, { approved: !r.approved }),
                    r.approved ? "Taken off the report" : "Approved — it will appear on the report")}>
                  {r.approved ? "Unapprove" : "✓ Approve"}
                </button>
                <button style={{ ...btn(false), padding: "3px 8px", fontSize: 10.5 }} disabled={busy}
                  onClick={() => setEditing(r)}>Edit</button>
                {quoteBtn(r)}
                <button style={{ ...btn(false), padding: "3px 8px", fontSize: 10.5, color: "#a32a24" }} disabled={busy}
                  onClick={() => guard(() => deleteRec(companyId, r.id), "Removed")}>Remove</button>
              </Row>
            )))}
          </>
        )}
      </div>
    </div>
  );
}

/* Add / edit, shared so the two paths cannot drift — the same reason the
   backend has one normaliseRec. */
function RecForm({ initial, busy, onSave, onCancel }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [detail, setDetail] = useState(initial?.detail || "");
  const [cost, setCost] = useState(initial?.cost == null ? "" : String(initial.cost));
  const [kind, setKind] = useState(initial?.kind || "recommendation");

  return (
    <div style={{ border: "1px solid var(--dt-secondary,#0b80a5)", borderRadius: 9, padding: 11, marginBottom: 8 }}>
      <input style={{ ...inp, marginBottom: 6 }} placeholder="What should they do?"
        value={title} onChange={(e) => setTitle(e.target.value)} />
      <textarea style={{ ...inp, minHeight: 54, marginBottom: 6 }} placeholder="Why it helps them — one or two sentences."
        value={detail} onChange={(e) => setDetail(e.target.value)} />
      <div style={{ display: "flex", gap: 7, alignItems: "center", flexWrap: "wrap" }}>
        <select style={{ ...inp, width: "auto" }} value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="quick-win">Quick win</option>
          <option value="recommendation">Suggested / out of budget</option>
        </select>
        {/* Blank is a real value — an unpriced recommendation prints no price
            rather than "$0", which would read as a promise that it is free. */}
        <input style={{ ...inp, width: 130 }} placeholder="Indicative $ (optional)"
          value={cost} onChange={(e) => setCost(e.target.value)} />
        <div style={{ flex: 1 }} />
        <button style={btn(false)} onClick={onCancel} disabled={busy}>Cancel</button>
        <button style={btn(true)} disabled={busy || !title.trim()}
          onClick={() => onSave({ title: title.trim(), detail: detail.trim(), kind, cost: cost === "" ? null : Number(cost) })}>
          Save
        </button>
      </div>
    </div>
  );
}
